import React from 'react';
import { Compass, Flag, Link2, MousePointerClick, UserPlus } from 'lucide-react';

/*
 * Acquisition journey views for the user detail page: where a user came from (the UTM links, ads and sites that
 * brought them to Ryzz, before and after signup) and which link installed each of their servers.
 * Data comes from GET /api/admin/users/:userId (acquisition, servers[].installedVia).
 */

function formatDateTime(iso) {
  if (!iso) return '—';
  const date = new Date(iso);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/** "discord / social" (+ " · october_launch") — the short label used in lists and the profile card. */
export function touchLabel(touch) {
  if (!touch) return null;
  const base = [touch.source, touch.medium].filter(Boolean).join(' / ') || 'unknown';
  return touch.campaign ? `${base} · ${touch.campaign}` : base;
}

function hostOf(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

function SourceBadge({ touch }) {
  const direct = touch.source === 'direct';
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold border ${direct
        ? 'bg-slate-500/10 text-slate-400 border-white/5'
        : 'bg-indigo-500/10 text-indigo-300 border-indigo-500/20'
        }`}
    >
      {touch.source || 'unknown'}
    </span>
  );
}

function Detail({ label, value, mono = false, title }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="text-slate-500 shrink-0">{label}</span>
      <span className={`text-slate-300 text-right break-all ${mono ? 'font-mono text-[11px]' : ''}`} title={title}>
        {value || '—'}
      </span>
    </div>
  );
}

/** Every field of one touch, as label / value rows. */
function TouchDetails({ touch }) {
  const clickIds = Object.entries(touch.clickIds || {});
  return (
    <div className="space-y-2 text-xs">
      <Detail label="Source" value={touch.source} />
      <Detail label="Medium" value={touch.medium} />
      <Detail label="Campaign" value={touch.campaign} />
      {touch.content && <Detail label="Content" value={touch.content} />}
      {touch.term && <Detail label="Term" value={touch.term} />}
      {touch.utmId && <Detail label="Campaign ID" value={touch.utmId} />}
      <Detail label="Landing page" value={touch.landingPage} mono />
      <Detail label="Referrer" value={touch.referrer ? hostOf(touch.referrer) : ''} title={touch.referrer} />
      {clickIds.length > 0 && (
        <Detail label="Ad click" value={clickIds.map(([k, v]) => `${k}: ${v.slice(0, 18)}${v.length > 18 ? '…' : ''}`).join(', ')} mono />
      )}
      <Detail label="Date" value={formatDateTime(touch.created)} />
    </div>
  );
}

function TouchCard({ icon, title, hint, touch, emptyText }) {
  return (
    <div className="glass-panel rounded-2xl p-5 border-white/[0.05]">
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <h3 className="text-sm font-black text-white">{title}</h3>
      </div>
      <p className="text-[10px] text-slate-500 mb-4">{hint}</p>
      {touch ? (
        <TouchDetails touch={touch} />
      ) : (
        <p className="text-xs text-slate-500">{emptyText}</p>
      )}
    </div>
  );
}

/** The "Acquisition" tab: first / last touch and the full journey, oldest first. */
export function AcquisitionTab({ acquisition }) {
  const journey = acquisition?.journey || [];
  const tracked = Boolean(acquisition?.tracked);

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <TouchCard
          icon={<Flag className="w-4 h-4 text-emerald-400" />}
          title="First touch"
          hint="What first brought this user to Ryzz (acquisition source)"
          touch={acquisition?.firstTouch}
          emptyText={tracked ? 'Joined before tracking started, so the original source is unknown.' : 'No journey recorded yet.'}
        />
        <TouchCard
          icon={<MousePointerClick className="w-4 h-4 text-indigo-400" />}
          title="Last touch"
          hint="The most recent marketing link, ad or site that brought them back"
          touch={acquisition?.lastTouch}
          emptyText="No marketing visit recorded yet."
        />
      </div>

      <div className="glass-panel rounded-2xl p-5 border-white/[0.05]">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h3 className="text-sm font-black text-white flex items-center gap-1.5">
              <Compass className="w-4 h-4 text-purple-400" />
              Journey
            </h3>
            <p className="text-[10px] text-slate-500 mt-0.5">
              Every recorded visit from a link, ad or other site, oldest first (max 100)
            </p>
          </div>
          <span className="text-[10px] font-bold text-slate-400 bg-slate-900/60 border border-white/5 px-2 py-1 rounded-lg shrink-0">
            {journey.length} {journey.length === 1 ? 'touch' : 'touches'}
          </span>
        </div>

        {journey.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-white/5">
                  <th className="py-2 pr-3 font-bold">#</th>
                  <th className="py-2 pr-3 font-bold">Date</th>
                  <th className="py-2 pr-3 font-bold">Source</th>
                  <th className="py-2 pr-3 font-bold">Medium</th>
                  <th className="py-2 pr-3 font-bold">Campaign</th>
                  <th className="py-2 pr-3 font-bold">Content</th>
                  <th className="py-2 pr-3 font-bold">Landing page</th>
                  <th className="py-2 font-bold">Referrer</th>
                </tr>
              </thead>
              <tbody>
                {journey.map((t, i) => (
                  <tr key={`${t.created}-${i}`} className={i < journey.length - 1 ? 'border-b border-white/[0.04]' : ''}>
                    <td className="py-2.5 pr-3 text-slate-600 font-mono">{i + 1}</td>
                    <td className="py-2.5 pr-3 text-slate-400 whitespace-nowrap">{formatDateTime(t.created)}</td>
                    <td className="py-2.5 pr-3"><SourceBadge touch={t} /></td>
                    <td className="py-2.5 pr-3 text-slate-300">{t.medium || '—'}</td>
                    <td className="py-2.5 pr-3 text-slate-300">{t.campaign || '—'}</td>
                    <td className="py-2.5 pr-3 text-slate-400">{t.content || '—'}</td>
                    <td className="py-2.5 pr-3 text-slate-400 font-mono text-[11px]">{t.landingPage || '—'}</td>
                    <td className="py-2.5 text-slate-400" title={t.referrer}>{t.referrer ? hostOf(t.referrer) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-10">
            <Compass className="w-10 h-10 text-slate-700 mx-auto mb-2" />
            <p className="text-xs text-slate-400 font-bold">No touches recorded</p>
            <p className="text-[10px] text-slate-500 mt-1 max-w-sm mx-auto">
              {tracked
                ? 'This user joined before tracking started, or has not arrived through a tracked link since.'
                : 'This user has no journey yet. It is created at their next login, or by the backfill script.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

/** Server tab card: which link (UTMs) and which referrer brought the bot into this server. */
export function InstalledViaCard({ installedVia }) {
  const acquisition = installedVia?.acquisition;
  const referrer = installedVia?.referrerDiscordId;

  return (
    <div className="glass-panel rounded-2xl p-5 border-white/[0.05]">
      <div className="flex items-center gap-2 mb-1">
        <Link2 className="w-4 h-4 text-indigo-400" />
        <h3 className="text-sm font-black text-white">Installed via</h3>
      </div>
      <p className="text-[10px] text-slate-500 mb-4">The link that added the bot to this server</p>

      {acquisition ? (
        <TouchDetails touch={acquisition} />
      ) : (
        <p className="text-xs text-slate-500">
          No campaign link recorded (installed through a link without UTMs, from the dashboard, or before tracking started).
        </p>
      )}

      {(referrer || installedVia?.installerDiscordId) && (
        <div className="space-y-2 text-xs border-t border-white/5 mt-4 pt-4">
          {installedVia?.installerDiscordId && <Detail label="Installed by" value={installedVia.installerDiscordId} mono />}
          {installedVia?.installedAt && <Detail label="Installed on" value={formatDateTime(installedVia.installedAt)} />}
          {referrer && (
            <div className="flex items-start justify-between gap-4">
              <span className="text-slate-500 shrink-0 flex items-center gap-1">
                <UserPlus className="w-3 h-3" />
                Referred by
              </span>
              <span className="text-amber-300 text-right font-mono text-[11px]">{referrer}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
